import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Checkbox,
    FormControlLabel,
    Tabs,
    Tab,
    CircularProgress,
    CardContent,
    Chip,
    Stack,
    Divider
} from '@mui/material';
import { FetchRoles, FetchOrganizations, FetchPermissions, CreateRole, UpdateRole } from '../../../../services/api';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const CreateRoleForm = ({ onClose, onSuccess, initialData }) => {
    const isEditMode = !!initialData;
    const [permissionTab, setPermissionTab] = useState(0);
    const [permissions, setPermissions] = useState([]);
    const [startLoading, setStartLoading] = useState(false);

    // Master data
    const [organizations, setOrganizations] = useState([]);

    // Alert state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    const token = localStorage.getItem('accessToken');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setStartLoading(true);
        try {
            // Fetch permissions and organizations
            // Use limit: 1000 to fetch all for selection
            const [permResp, orgResp] = await Promise.all([
                FetchPermissions({ limit: 1000 }, token),
                FetchOrganizations(token, { limit: 1000 })
            ]);

            if (permResp?.data?.permissions) {
                setPermissions(permResp.data.permissions);
            } else if (Array.isArray(permResp?.data)) {
                setPermissions(permResp.data);
            } else if (permResp?.data?.data) {
                setPermissions(permResp.data.data);
            }

            if (orgResp?.data?.data) {
                setOrganizations(orgResp.data.data);
            } else if (Array.isArray(orgResp?.data)) {
                setOrganizations(orgResp.data);
            }

        } catch (error) {
            console.error("Error loading data", error);
        }
        setStartLoading(false);
    };

    const validationSchema = Yup.object({
        name: Yup.string().trim().min(2, 'Role name must be at least 2 characters').max(100, 'Role name too long').required('Role Name is required'),
        description: Yup.string().trim().max(500, 'Description too long').required('Description is required'),
        organizationId: Yup.string().nullable(),
        roleCategory: Yup.string().required('Category is required'),
        selectedPermissions: Yup.array().of(Yup.string())
    });

    const getInitialPermissions = () => {
        if (!initialData) return [];
        if (initialData.permissionIds && initialData.permissionIds.length > 0) return initialData.permissionIds;
        if (initialData.permissions && initialData.permissions.length > 0) return initialData.permissions.map(p => p.id);
        return [];
    };

    const formik = useFormik({
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            roleCategory: initialData?.roleCategory || 'internal',
            organizationId: initialData?.organizationId || '', // Handle null?
            selectedPermissions: getInitialPermissions(),
            applicationScope: initialData?.applicationScope || 'global'
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            const payload = {
                name: values.name,
                description: values.description,
                roleCategory: values.roleCategory,
                applicationScope: values.applicationScope,
                organizationId: values.organizationId || null,
                permissionIds: values.selectedPermissions,
                isActive: initialData ? initialData.isActive : true
            };

            try {
                let resp;
                if (isEditMode) {
                    const id = initialData.id || initialData._id;
                    resp = await UpdateRole(id, payload, token);
                } else {
                    resp = await CreateRole(payload, token);
                }

                if (resp?.data || resp?.status === 200 || resp?.status === 201) {
                    setAlertMessage(`Role ${isEditMode ? 'updated' : 'created'} successfully`);
                    setAlertSeverity('success');
                    setAlertOpen(true);
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        if (onClose) onClose();
                    }, 1000);
                } else {
                    setAlertMessage(resp?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} role`);
                    setAlertSeverity('error');
                    setAlertOpen(true);
                }
            } catch (error) {
                console.error("Submit error", error);
                setAlertMessage('An unexpected error occurred');
                setAlertSeverity('error');
                setAlertOpen(true);
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Permission Logic
    const groupedPermissions = permissions.reduce((acc, perm) => {
        // Fallbacks for module grouping
        const group = perm.module || perm.resource || (perm.group ? perm.group : 'Other');
        if (!acc[group]) acc[group] = [];
        acc[group].push(perm);
        return acc;
    }, {});

    const permissionGroups = Object.keys(groupedPermissions).sort();

    const handlePermissionToggle = (permId) => {
        const currentSelected = formik.values.selectedPermissions;
        if (currentSelected.includes(permId)) {
            formik.setFieldValue('selectedPermissions', currentSelected.filter(id => id !== permId));
        } else {
            formik.setFieldValue('selectedPermissions', [...currentSelected, permId]);
        }
    };

    const handleSelectAllGroup = (group) => {
        const groupPermIds = groupedPermissions[group].map(p => p.id);
        const currentSelected = formik.values.selectedPermissions;
        const allSelected = groupPermIds.every(id => currentSelected.includes(id));

        if (allSelected) {
            // Deselect all
            formik.setFieldValue('selectedPermissions', currentSelected.filter(id => !groupPermIds.includes(id)));
        } else {
            // Select all
            const newSelected = [...currentSelected];
            groupPermIds.forEach(id => {
                if (!newSelected.includes(id)) newSelected.push(id);
            });
            formik.setFieldValue('selectedPermissions', newSelected);
        }
    };

    if (startLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <CustomAlert open={alertOpen} handleClose={() => setAlertOpen(false)} message={alertMessage} severity={alertSeverity} />

            <Box p={2} borderBottom={1} borderColor="divider" display="flex" justifyContent="center">
                <Typography variant="h6" fontWeight="bold">{isEditMode ? 'Edit Role' : 'Create New Role'}</Typography>
            </Box>

            <CardContent sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                id="name"
                                name="name"
                                label="Role Name *"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                id="description"
                                name="description"
                                label="Description *"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Organization</InputLabel>
                                <Select
                                    id="organizationId"
                                    name="organizationId"
                                    value={formik.values.organizationId}
                                    label="Organization"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value=""><em>None (Global)</em></MenuItem>
                                    {organizations.map(org => (
                                        <MenuItem key={org.id || org._id} value={org.id || org._id}>{org.organizationName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    id="roleCategory"
                                    name="roleCategory"
                                    value={formik.values.roleCategory}
                                    label="Category"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value="internal">Internal</MenuItem>
                                    <MenuItem value="external">External</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box mt={4}>
                        <Divider textAlign="left"><Typography variant="subtitle2" color="textSecondary">PERMISSIONS</Typography></Divider>

                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                Selected: <b>{formik.values.selectedPermissions.length}</b> permissions
                            </Typography>
                        </Box>

                        <Tabs
                            value={permissionTab}
                            onChange={(e, v) => setPermissionTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                        >
                            {permissionGroups.map((group, index) => (
                                <Tab key={group} label={group} />
                            ))}
                        </Tabs>

                        {permissionGroups.map((group, index) => (
                            <div key={group} role="tabpanel" hidden={permissionTab !== index}>
                                {permissionTab === index && (
                                    <Box bgcolor="#fafafa" p={2} borderRadius={2} border={1} borderColor="#eee">
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Typography variant="subtitle1" fontWeight="bold">{group}</Typography>
                                            <Button size="small" variant="outlined" onClick={() => handleSelectAllGroup(group)}>
                                                Select/Deselect All
                                            </Button>
                                        </Box>
                                        <Grid container spacing={1}>
                                            {groupedPermissions[group].map(perm => (
                                                <Grid item xs={12} sm={6} md={4} key={perm.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={formik.values.selectedPermissions.includes(perm.id)}
                                                                onChange={() => handlePermissionToggle(perm.id)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2">
                                                                {perm.action}
                                                                <Typography component="span" variant="caption" display="block" color="textSecondary">
                                                                    {perm.description || perm.name}
                                                                </Typography>
                                                            </Typography>
                                                        }
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </div>
                        ))}
                    </Box>
                </form>
            </CardContent>

            <Box p={2} borderTop={1} borderColor="divider" display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onClose} variant="outlined" disabled={formik.isSubmitting}>Cancel</Button>
                <Button
                    onClick={formik.handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.isValid}
                    startIcon={formik.isSubmitting && <CircularProgress size={20} color="inherit" />}
                >
                    {isEditMode ? 'Update Role' : 'Create Role'}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateRoleForm;
